import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Line } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import moment from 'moment';
import * as adminService from '../../services/admin';

const SecurityDashboard = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [statistics, setStatistics] = useState(null);
    const [loginHistory, setLoginHistory] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [settings, setSettings] = useState([]);
    const [ipWhitelist, setIpWhitelist] = useState([]);
    const [activeSessions, setActiveSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showIpModal, setShowIpModal] = useState(false);
    const [ipForm, setIpForm] = useState({
        ip_address: '',
        label: '',
        description: '',
        is_active: true
    });

    useEffect(() => {
        loadStatistics();
        loadLoginHistory();
        loadAlerts();
        loadSettings();
        loadIpWhitelist();
        loadActiveSessions();
    }, []);

    const loadStatistics = async () => {
        try {
            const response = await adminService.getSecurityStatistics({ days: 30 });
            if (response.status === 'success') {
                setStatistics(response.data);
            }
        } catch (error) {
            console.error('Error loading statistics:', error);
        }
    };

    const loadLoginHistory = async () => {
        try {
            const response = await adminService.getLoginHistory({});
            if (response.status === 'success') {
                setLoginHistory(response.data);
            }
        } catch (error) {
            console.error('Error loading login history:', error);
        }
    };

    const loadAlerts = async () => {
        try {
            const response = await adminService.getSecurityAlerts({ is_resolved: false });
            if (response.status === 'success') {
                setAlerts(response.data);
            }
        } catch (error) {
            console.error('Error loading alerts:', error);
        }
    };

    const loadSettings = async () => {
        try {
            const response = await adminService.getSecuritySettings();
            if (response.status === 'success') {
                setSettings(response.data);
            }
        } catch (error) {
            console.error('Error loading settings:', error);
        }
    };

    const loadIpWhitelist = async () => {
        try {
            const response = await adminService.getIpWhitelist();
            if (response.status === 'success') {
                setIpWhitelist(response.data);
            }
        } catch (error) {
            console.error('Error loading IP whitelist:', error);
        }
    };

    const loadActiveSessions = async () => {
        try {
            const response = await adminService.getActiveSessions();
            if (response.status === 'success') {
                setActiveSessions(response.data);
            }
        } catch (error) {
            console.error('Error loading active sessions:', error);
        }
    };

    const handleResolveAlert = async (id) => {
        try {
            const response = await adminService.resolveSecurityAlert(id);
            if (response.status === 'success') {
                toast.success('Alert resolved successfully');
                loadAlerts();
            }
        } catch (error) {
            console.error('Error resolving alert:', error);
            toast.error('Failed to resolve alert');
        }
    };

    const handleUpdateSetting = async (id, value) => {
        try {
            const response = await adminService.updateSecuritySetting(id, { setting_value: value });
            if (response.status === 'success') {
                toast.success('Setting updated successfully');
                loadSettings();
            }
        } catch (error) {
            console.error('Error updating setting:', error);
            toast.error('Failed to update setting');
        }
    };

    const handleAddIp = async () => {
        if (!ipForm.ip_address) {
            toast.error('Please enter IP address');
            return;
        }

        try {
            const response = await adminService.addIpToWhitelist(ipForm);
            if (response.status === 'success') {
                toast.success('IP added to whitelist');
                setShowIpModal(false);
                setIpForm({ ip_address: '', label: '', description: '', is_active: true });
                loadIpWhitelist();
            }
        } catch (error) {
            console.error('Error adding IP:', error);
            toast.error('Failed to add IP');
        }
    };

    const handleRemoveIp = async (id) => {
        const result = await Swal.fire({
            title: 'Remove IP?',
            text: 'This IP will be removed from whitelist',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, remove it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await adminService.removeIpFromWhitelist(id);
                if (response.status === 'success') {
                    toast.success('IP removed from whitelist');
                    loadIpWhitelist();
                }
            } catch (error) {
                console.error('Error removing IP:', error);
                toast.error('Failed to remove IP');
            }
        }
    };

    const getSeverityBadge = (severity) => {
        const badges = {
            'low': 'bg-info',
            'medium': 'bg-warning',
            'high': 'bg-danger',
            'critical': 'bg-dark'
        };
        return badges[severity] || 'bg-secondary';
    };

    const loginHistoryColumns = [
        { headerName: 'User', field: 'user.name', sortable: true, filter: true, width: 150 },
        { headerName: 'Email', field: 'email', sortable: true, filter: true, width: 200 },
        { headerName: 'IP Address', field: 'ip_address', sortable: true, filter: true, width: 140 },
        {
            headerName: 'Status',
            field: 'login_status',
            sortable: true,
            filter: true,
            width: 120,
            cellRenderer: (params) => (
                <span className={`badge ${params.value === 'success' ? 'bg-success' : 'bg-danger'} text-capitalize`}>
                    {params.value}
                </span>
            )
        },
        {
            headerName: 'Login Time',
            field: 'login_at',
            sortable: true,
            filter: true,
            width: 180,
            cellRenderer: (params) => moment(params.value).format('DD-MMM-YYYY HH:mm:ss')
        },
        { headerName: 'Location', field: 'location', sortable: true, filter: true, width: 150 }
    ];

    const renderOverview = () => {
        if (!statistics) return null;

        const loginTrendData = {
            labels: statistics.login_trend.map(item => moment(item.date).format('MMM DD')),
            datasets: [
                {
                    label: 'Successful Logins',
                    data: statistics.login_trend.map(item => item.successful),
                    borderColor: '#28a745',
                    backgroundColor: 'rgba(40, 167, 69, 0.1)',
                    tension: 0.4
                },
                {
                    label: 'Failed Logins',
                    data: statistics.login_trend.map(item => item.failed),
                    borderColor: '#dc3545',
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    tension: 0.4
                }
            ]
        };

        return (
            <div>
                <div className="row mb-4">
                    <div className="col-md-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body text-center">
                                <h6>Total Logins (30d)</h6>
                                <h2>{statistics.statistics.total_logins}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-success text-white">
                            <div className="card-body text-center">
                                <h6>Successful Logins</h6>
                                <h2>{statistics.statistics.successful_logins}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-danger text-white">
                            <div className="card-body text-center">
                                <h6>Failed Logins</h6>
                                <h2>{statistics.statistics.failed_logins}</h2>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-warning text-white">
                            <div className="card-body text-center">
                                <h6>Active Alerts</h6>
                                <h2>{statistics.statistics.active_alerts}</h2>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card mb-4">
                    <div className="card-header">
                        <h6 className="mb-0">Login Trend (Last 7 Days)</h6>
                    </div>
                    <div className="card-body">
                        <Line data={loginTrendData} />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-6">
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">Top Failed Login IPs</h6>
                            </div>
                            <div className="card-body">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>IP Address</th>
                                            <th className="text-end">Failed Attempts</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {statistics.top_failed_ips.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.ip_address}</td>
                                                <td className="text-end">
                                                    <span className="badge bg-danger">{item.count}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card">
                            <div className="card-header">
                                <h6 className="mb-0">Active Sessions</h6>
                            </div>
                            <div className="card-body">
                                <p><strong>Total Active Sessions:</strong> {activeSessions.length}</p>
                                <p><strong>Unique Users Online:</strong> {new Set(activeSessions.map(s => s.user_id)).size}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid mt-3">
            <div className="card">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fa fa-shield-alt"></i> Security Dashboard
                    </h5>
                </div>
                <div className="card-body">
                    <ul className="nav nav-tabs mb-3">
                        <li className="nav-item">
                            <button className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
                                <i className="fa fa-tachometer-alt me-2"></i>Overview
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className={`nav-link ${activeTab === 'login_history' ? 'active' : ''}`} onClick={() => setActiveTab('login_history')}>
                                <i className="fa fa-history me-2"></i>Login History
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className={`nav-link ${activeTab === 'alerts' ? 'active' : ''}`} onClick={() => setActiveTab('alerts')}>
                                <i className="fa fa-exclamation-triangle me-2"></i>Alerts ({alerts.length})
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
                                <i className="fa fa-cog me-2"></i>Settings
                            </button>
                        </li>
                        <li className="nav-item">
                            <button className={`nav-link ${activeTab === 'ip_whitelist' ? 'active' : ''}`} onClick={() => setActiveTab('ip_whitelist')}>
                                <i className="fa fa-list me-2"></i>IP Whitelist
                            </button>
                        </li>
                    </ul>

                    {activeTab === 'overview' && renderOverview()}

                    {activeTab === 'login_history' && (
                        <div className="ag-theme-alpine" style={{ height: '500px', width: '100%' }}>
                            <AgGridReact
                                rowData={loginHistory}
                                columnDefs={loginHistoryColumns}
                                pagination={true}
                                paginationPageSize={20}
                                domLayout='autoHeight'
                            />
                        </div>
                    )}

                    {activeTab === 'alerts' && (
                        <div>
                            {alerts.length > 0 ? (
                                <div className="table-responsive">
                                    <table className="table table-bordered">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Severity</th>
                                                <th>Type</th>
                                                <th>Title</th>
                                                <th>Description</th>
                                                <th>Created</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {alerts.map(alert => (
                                                <tr key={alert.id}>
                                                    <td>
                                                        <span className={`badge ${getSeverityBadge(alert.severity)} text-capitalize`}>
                                                            {alert.severity}
                                                        </span>
                                                    </td>
                                                    <td className="text-capitalize">{alert.alert_type.replace(/_/g, ' ')}</td>
                                                    <td>{alert.title}</td>
                                                    <td>{alert.description}</td>
                                                    <td>{moment(alert.created_at).format('DD-MMM-YYYY HH:mm')}</td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => handleResolveAlert(alert.id)}
                                                        >
                                                            Resolve
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center text-muted py-5">
                                    <i className="fa fa-check-circle fa-3x mb-3"></i>
                                    <p>No active security alerts</p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'settings' && (
                        <div className="table-responsive">
                            <table className="table table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th>Setting</th>
                                        <th>Description</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {settings.map(setting => (
                                        <tr key={setting.id}>
                                            <td>{setting.setting_key.replace(/_/g, ' ').toUpperCase()}</td>
                                            <td>{setting.description}</td>
                                            <td>
                                                {setting.setting_type === 'boolean' ? (
                                                    <div className="form-check form-switch">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={setting.value}
                                                            onChange={(e) => handleUpdateSetting(setting.id, e.target.checked)}
                                                        />
                                                    </div>
                                                ) : (
                                                    <input
                                                        type={setting.setting_type === 'integer' ? 'number' : 'text'}
                                                        className="form-control form-control-sm"
                                                        value={setting.setting_value}
                                                        onChange={(e) => handleUpdateSetting(setting.id, e.target.value)}
                                                        style={{ width: '150px' }}
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeTab === 'ip_whitelist' && (
                        <div>
                            <button className="btn btn-primary mb-3" onClick={() => setShowIpModal(true)}>
                                <i className="fa fa-plus"></i> Add IP
                            </button>
                            <div className="table-responsive">
                                <table className="table table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>IP Address</th>
                                            <th>Label</th>
                                            <th>Description</th>
                                            <th>Status</th>
                                            <th>Added By</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ipWhitelist.map(ip => (
                                            <tr key={ip.id}>
                                                <td><code>{ip.ip_address}</code></td>
                                                <td>{ip.label}</td>
                                                <td>{ip.description}</td>
                                                <td>
                                                    <span className={`badge ${ip.is_active ? 'bg-success' : 'bg-secondary'}`}>
                                                        {ip.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>{ip.created_by_name}</td>
                                                <td>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleRemoveIp(ip.id)}
                                                    >
                                                        <i className="fa fa-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showIpModal && (
                <div className="modal show d-block" tabIndex="-1" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add IP to Whitelist</h5>
                                <button type="button" className="btn-close" onClick={() => setShowIpModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">IP Address *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={ipForm.ip_address}
                                        onChange={(e) => setIpForm({ ...ipForm, ip_address: e.target.value })}
                                        placeholder="e.g., 192.168.1.1"
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Label</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={ipForm.label}
                                        onChange={(e) => setIpForm({ ...ipForm, label: e.target.value })}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        className="form-control"
                                        rows="3"
                                        value={ipForm.description}
                                        onChange={(e) => setIpForm({ ...ipForm, description: e.target.value })}
                                    ></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowIpModal(false)}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary" onClick={handleAddIp}>
                                    Add IP
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SecurityDashboard;
